import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, CookingPot, Microwave, Soup, Sandwich, Droplets, Sparkles, Wallet, Crown, Users, Baby, RotateCcw, ChefHat, GraduationCap, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { GUIDE_STEP_METHODS, GUIDE_STEP_NEEDS, GUIDE_STEP_EXPERIENCE } from "@/lib/site";
import ProductCard from "@/components/products/ProductCard";

const METHOD_ICONS = { Mangal: Flame, Tava: CookingPot, Fırın: Microwave, Tencere: Soup, Burger: Sandwich, Haşlama: Soup };
const NEED_ICONS = {
  "Yumuşak et": Droplets,
  "Yoğun lezzet": Sparkles,
  "Ekonomik tercih": Wallet,
  "Premium deneyim": Crown,
  "Kalabalık sofra": Users,
  "Çocuklar için uygun": Baby,
};
const EXP_ICONS = { "İlk kez yapıyorum": GraduationCap, "Biraz tecrübeliyim": ChefHat, "Usta işi istiyorum": Star };

function scoreProduct(p, sel) {
  let score = 0;
  const method = sel.method === "Haşlama" ? "Tencere" : sel.method;
  if (sel.method === "Burger") {
    if (["burger-koftesi", "kasap-kofte", "dana-kiyma"].includes(p.slug)) score += 3;
  } else if (p.cooking_methods?.includes(method)) {
    score += 3;
  }
  switch (sel.need) {
    case "Yumuşak et": if (p.tenderness >= 4) score += 2; break;
    case "Yoğun lezzet": if (p.flavor >= 4) score += 2; break;
    case "Ekonomik tercih": if (p.premium <= 2) score += 2; break;
    case "Premium deneyim": if (p.premium >= 4) score += 2; break;
    case "Kalabalık sofra": if (p.ease >= 4 || p.tags?.includes("Aile İçin")) score += 2; break;
    case "Çocuklar için uygun": if (p.suitable_for?.includes("Çocuklar için")) score += 2; break;
    default: break;
  }
  if (sel.experience === "İlk kez yapıyorum") {
    if (p.difficulty === "Kolay") score += 2;
    if (p.difficulty === "Usta işi") score -= 1;
  } else if (sel.experience === "Biraz tecrübeliyim") {
    if (["Kolay", "Orta"].includes(p.difficulty)) score += 1;
  } else if (sel.experience === "Usta işi istiyorum") {
    if (p.difficulty === "Usta işi") score += 2;
  }
  return score;
}

const OptionButton = ({ label, Icon, active, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={cn(
      "group flex items-center gap-3 border px-4 py-4 text-left transition-all duration-200",
      active
        ? "border-bordo bg-bordo text-cream"
        : "border-dashed border-charcoal/25 bg-paper text-charcoal hover:border-bordo hover:bg-bordo/5"
    )}
  >
    <Icon className={cn("h-5 w-5 flex-shrink-0 transition-colors", active ? "text-cream" : "text-bordo")} />
    <span className="text-sm font-medium">{label}</span>
  </button>
);

export const SmartGuide = ({ products = [] }) => {
  const [step, setStep] = useState(1);
  const [sel, setSel] = useState({ method: null, need: null, experience: null });

  const results = useMemo(() => {
    if (step < 4) return [];
    const scored = products
      .map((p) => ({ p, s: scoreProduct(p, sel) }))
      .sort((a, b) => b.s - a.s)
      .filter((x) => x.s > 0)
      .map((x) => x.p);
    if (scored.length >= 3) return scored.slice(0, 5);
    const fill = products.filter((p) => p.featured && !scored.includes(p));
    return [...scored, ...fill].slice(0, 5);
  }, [step, sel, products]);

  const reset = () => {
    setSel({ method: null, need: null, experience: null });
    setStep(1);
  };

  const pick = (key, value, nextStep) => {
    setSel((prev) => ({ ...prev, [key]: value }));
    setTimeout(() => setStep(nextStep), 180);
  };

  const stepMeta = [
    { num: "01", q: "Pişirme yöntemini seçin", opts: GUIDE_STEP_METHODS, key: "method", icons: METHOD_ICONS, fallback: Flame, prefix: "guide-method" },
    { num: "02", q: "Ne arıyorsunuz?", opts: GUIDE_STEP_NEEDS, key: "need", icons: NEED_ICONS, fallback: Sparkles, prefix: "guide-need" },
    { num: "03", q: "Deneyim seviyeniz", opts: GUIDE_STEP_EXPERIENCE, key: "experience", icons: EXP_ICONS, fallback: Star, prefix: "guide-exp" },
  ];

  return (
    <div data-testid="smart-guide" className="border border-dashed border-bordo/30 bg-paper">
      <div className="grid lg:grid-cols-12">
        {/* Sol — başlık kolonu (dergi) */}
        <div className="border-b border-dashed border-bordo/30 bg-charcoal p-8 sm:p-10 lg:col-span-4 lg:border-b-0 lg:border-r">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-copper">Akıllı Rehber</span>
          <h3 className="mt-4 font-serif text-4xl leading-[0.95] text-cream sm:text-5xl">
            Nasıl<br /><span className="italic text-copper">Pişireceksiniz?</span>
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-cream/60">
            Üç soruyla mutfağınıza ve damak zevkinize en uygun etleri birlikte bulalım.
          </p>
          {step < 4 && (
            <div className="mt-8 flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <span key={s} className={cn("h-1 transition-all", s === step ? "w-10 bg-copper" : s < step ? "w-6 bg-copper/50" : "w-6 bg-cream/15")} />
              ))}
              <span className="ml-2 font-mono text-xs text-cream/40">{Math.min(step, 3)}/3</span>
            </div>
          )}
        </div>

        {/* Sağ — adım içeriği */}
        <div className="p-6 sm:p-10 lg:col-span-8">
          <AnimatePresence mode="wait">
            {step < 4 ? (
              <motion.div key={`step-${step}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                <div className="mb-6 flex items-baseline gap-4">
                  <span className="font-serif text-6xl leading-none text-bordo/25">{stepMeta[step - 1].num}</span>
                  <p className="font-mono text-sm uppercase tracking-wider text-charcoal/70">{stepMeta[step - 1].q}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {stepMeta[step - 1].opts.map((o) => (
                    <OptionButton
                      key={o}
                      label={o}
                      Icon={stepMeta[step - 1].icons[o] || stepMeta[step - 1].fallback}
                      active={sel[stepMeta[step - 1].key] === o}
                      onClick={() => pick(stepMeta[step - 1].key, o, step + 1)}
                      testid={`${stepMeta[step - 1].prefix}-${o}`}
                    />
                  ))}
                </div>
                {step > 1 && (
                  <button onClick={() => setStep((s) => s - 1)} className="mt-6 font-mono text-xs uppercase tracking-wider text-charcoal/40 hover:text-bordo transition-colors">
                    ← Önceki adım
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.div key="results" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <span className="font-mono text-xs uppercase tracking-wider text-copper">Size Özel Önerimiz</span>
                    <p className="mt-1 font-mono text-sm text-charcoal/60">{sel.method} · {sel.need} · {sel.experience}</p>
                  </div>
                  <button
                    data-testid="guide-reset"
                    onClick={reset}
                    className="inline-flex w-fit items-center gap-2 rounded-sm border border-charcoal/20 px-4 py-2 font-mono text-xs uppercase tracking-wider text-charcoal hover:border-bordo hover:text-bordo transition-colors"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Baştan Başla
                  </button>
                </div>
                <div data-testid="guide-results" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {results.map((p) => <ProductCard key={p.slug} product={p} />)}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default SmartGuide;
